import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import isEqual from "lodash/isEqual";

import { Dialog, DialogContent, Typography } from "@mui/material";

import DependentMutationInput from "./DependentMutationInput/DependentMutationInput";
import DialogTitle from "../../../../containers/DialogTitle/DialogTitle";
import DialogActions from "../../../../containers/DialogActions/DialogActions";
import ConfirmationChangesDialog from "../../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";

import { EmployeeCurrentDependentsMutationsApiService } from "../../../../services/employees/employees-current-dependents-mutations-api.service";

import { EmployeeDataContext } from "../../EmployeeDataAndMutaions";

import { Props, State } from "./interfaces";
import { AddEditButton } from "./styles";
import { EmployeeDependentMutationSTO } from "../../../../models/employee-dependents/mutations/employee-dependent-mutation-sto";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { EmployeeDependentMutationsApiService } from "../../../../services/employees/employees-dependent-mutations-api.service";

const AddEditDependentMutation = ({ original, mutation, hasMutation, handleReload }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.data_and_documents"; // tRP - translation root path

  const { employee, forManager } = useContext(EmployeeDataContext);

  const [state, setState] = useState<State>({
    original: null,
    mutation: null,
    resetMutation: null,
    isDialogOpen: false,
    isConfirmationChangesDialogOpen: false,
    loading: false,
    error: "",
  });

  useEffect(() => {
    if (!original || !mutation) return;

    setState((state) => ({
      ...state,
      original,
      mutation,
      resetMutation: mutation,
    }));
  }, [original, mutation]);

  const setIsDialogOpen = (value: boolean) => setState((state) => ({ ...state, isDialogOpen: value }));

  const setIsConfirmationChangesDialog = (value: boolean) =>
    setState((state) => ({ ...state, isConfirmationChangesDialogOpen: value }));

  const closeMainDialog = () => {
    !isEqual(state.mutation, state.resetMutation) ? setIsConfirmationChangesDialog(true) : setIsDialogOpen(false);
  };

  const handleDiscardChanges = () =>
    setState((state) => ({
      ...state,
      mutation,
      resetMutation: mutation,
      isDialogOpen: false,
      isConfirmationChangesDialogOpen: false,
      error: "",
    }));

  const handleSave = async (): Promise<void> => {
    setState((state) => ({ ...state, loading: true }));

    try {
      const apiService = forManager
        ? new EmployeeDependentMutationsApiService()
        : new EmployeeCurrentDependentsMutationsApiService();

      if (forManager) {
        apiService.useSaveItemsPath("/ApproveEmployeeDependentMutations");
      }

      const { error } = await apiService.saveItems([state.mutation as EmployeeDependentMutationSTO]);

      if (!error) await handleReload();

      setState((state) => ({ ...state, error, isDialogOpen: false }));
    } catch (error: any) {
      setState((state) => ({ ...state, error: error?.message ?? error }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  };

  const handleChange = (field: string, value: string | boolean | number | Date) =>
    setState((state) => ({
      ...state,
      mutation: { ...state.mutation, [field]: value } as EmployeeDependentMutationSTO,
    }));

  return (
    <>
      <AddEditButton
        onClick={() => setIsDialogOpen(true)}
        children={
          <>
            <FontAwesomeIcon icon={faChevronRight} /> &nbsp;
            <Typography variant="subtitle2">
              {`${t(`${tRP}.common_keys.${hasMutation ? "edit_change_request" : "request_a_change"}`)}`}
            </Typography>
          </>
        }
      />
      <Dialog
        fullWidth
        open={state.isDialogOpen}
        onClose={closeMainDialog}
      >
        <DialogTitle
          title={`${employee?.name}: ${t(`${tRP}.employee_data.employee_dependents.edit_title`)}`}
          handleClose={closeMainDialog}
        />
        <DialogContent>
          <DependentMutationInput
            originalValue={state.original?.dependentType?.name}
            mutationValue={state.mutation?.dependentType?.name}
            mutationObject={state.mutation}
            handleChangeMutation={handleChange}
            field="dependentType"
          />
          <DependentMutationInput
            originalValue={state.original?.firstName}
            mutationValue={state.mutation?.firstName}
            handleChangeMutation={handleChange}
            field="firstName"
          />
          <DependentMutationInput
            originalValue={state.original?.lastName}
            mutationValue={state.mutation?.lastName}
            handleChangeMutation={handleChange}
            field="lastName"
          />
          <DependentMutationInput
            originalValue={state.original?.birthDate}
            mutationValue={state.mutation?.birthDate}
            handleChangeMutation={handleChange}
            field="birthDate"
          />
        </DialogContent>
        <DialogActions
          handleCancel={closeMainDialog}
          handleSave={handleSave}
          error={state.error}
          loading={state.loading}
          customSaveName={forManager ? `${t(`buttons.approve`)} & ${t(`buttons.save`)}` : `${t(`buttons.save`)}`}
        />
      </Dialog>
      <ConfirmationChangesDialog
        open={state.isConfirmationChangesDialogOpen}
        handleCancel={() => setIsConfirmationChangesDialog(false)}
        handleDiscard={handleDiscardChanges}
      />
    </>
  );
};

export default AddEditDependentMutation;
